/**
 * Email Helper Utilities
 * Provides functions to clean, extract, parse, and validate email strings across Spark.
 */

// RFC 5322 compliant regex for individual email validation
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

// Global regex to match email patterns inside text
const EMAIL_MATCH_REGEX = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/g;

/**
 * Checks if a string is a valid email address.
 *
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    return EMAIL_REGEX.test(email.trim());
}

/**
 * Strips formatting, display names, angle brackets, quotes, and mailto prefixes from a pasted email string.
 * e.g. 'Rifat Ahmed Khan <Rifat@vertexcybersecurity.com.au>' -> 'Rifat@vertexcybersecurity.com.au'
 * e.g. '“Rifat Ahmed Khan” <Rifat@vertexcybersecurity.com.au>' -> 'Rifat@vertexcybersecurity.com.au'
 * e.g. '<mailto:Rifat@vertexcybersecurity.com.au>' -> 'Rifat@vertexcybersecurity.com.au'
 *
 * @param {string} input
 * @returns {string}
 */
export function cleanEmailString(input) {
    if (!input || typeof input !== 'string') return '';
    let cleaned = input.trim();

    // Strip leading/trailing surrounding quotes (standard quotes, smart/curly quotes, guillemets)
    cleaned = cleaned.replace(/^["'“”‘’`«»‹›]+|["'“”‘’`«»‹›]+$/g, '').trim();

    // 1. Look for email inside angle brackets: e.g. <user@domain.com> or <mailto:user@domain.com>
    const angleMatch = cleaned.match(/<([^>]+@[^>]+)>/);
    if (angleMatch) {
        cleaned = angleMatch[1];
    } else {
        // 2. Look for email inside square brackets: e.g. [user@domain.com] or [mailto:user@domain.com]
        const bracketMatch = cleaned.match(/\[([^\]]+@[^\]]+)\]/);
        if (bracketMatch) {
            cleaned = bracketMatch[1];
        } else {
            // 3. Look for email inside parentheses: e.g. (user@domain.com) or (mailto:user@domain.com)
            const parenMatch = cleaned.match(/\(([^)]+@[^)]+)\)/);
            if (parenMatch) {
                cleaned = parenMatch[1];
            } else {
                // 4. Look for an email pattern anywhere in the string
                const emailMatch = cleaned.match(EMAIL_MATCH_REGEX);
                if (emailMatch && emailMatch.length > 0) {
                    cleaned = emailMatch[0];
                }
            }
        }
    }

    // Strip mailto: prefix if present
    cleaned = cleaned.replace(/^mailto:\s*/i, '');

    // Strip URL query parameters (e.g. ?subject=...)
    if (cleaned.includes('?')) {
        cleaned = cleaned.split('?')[0];
    }

    // Strip any remaining surrounding delimiters / quotes / spaces
    cleaned = cleaned.replace(/^[<(["'“”‘’`«»‹›\s]+|[>)\]"'“”‘’`«»‹›\s:;,]+$/g, '').trim();

    return cleaned;
}

/**
 * Extracts all valid email addresses from a string containing one or more emails.
 *
 * @param {string} input
 * @returns {string[]}
 */
export function extractEmails(input) {
    if (!input || typeof input !== 'string') return [];

    // First try splitting by common delimiters (newlines, semicolons, commas)
    const rawTokens = input.split(/[\r\n;,]+/).map(s => s.trim()).filter(Boolean);
    const emails = [];

    for (const token of rawTokens) {
        const cleaned = cleanEmailString(token);
        if (cleaned && isValidEmail(cleaned)) {
            if (!emails.includes(cleaned)) {
                emails.push(cleaned);
            }
        }
    }

    // Fallback: if token splitting did not find all emails, match via global regex
    if (emails.length === 0) {
        const matches = input.match(EMAIL_MATCH_REGEX);
        if (matches) {
            matches.forEach(m => {
                const c = cleanEmailString(m);
                if (c && isValidEmail(c) && !emails.includes(c)) {
                    emails.push(c);
                }
            });
        }
    }

    return emails;
}

/**
 * Parses an email recipient input into { displayName, email }.
 *
 * @param {string} input
 * @returns {{ displayName: string, email: string } | null}
 */
export function parseEmailRecipient(input) {
    if (!input || typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (!trimmed) return null;

    const email = cleanEmailString(trimmed);
    if (!isValidEmail(email)) return null;

    let displayName = email;
    const nameMatch = trimmed.match(/^([^<(["]+)[<(["]/);
    if (nameMatch) {
        const candidateName = nameMatch[1].replace(/["'“”‘’`«»‹›:,]/g, '').trim();
        if (candidateName && candidateName !== email) {
            displayName = candidateName;
        }
    }

    return { displayName, email };
}

/**
 * Determines whether a DOM element is an email input field in Spark.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export function isEmailInput(el) {
    if (!el) return false;
    if (typeof HTMLElement !== 'undefined' && !(el instanceof HTMLElement)) return false;

    const tagName = (el.tagName || '').toLowerCase();
    if (tagName !== 'input' && tagName !== 'textarea') return false;

    const inputType = (el.getAttribute('type') || el.type || 'text').toLowerCase();

    // Explicit email type
    if (inputType === 'email') return true;

    // Exclude non-textual input types
    if (['checkbox', 'radio', 'file', 'button', 'submit', 'reset', 'password', 'color', 'range', 'date', 'datetime-local', 'time', 'number'].includes(inputType)) {
        return false;
    }

    const id = (el.id || '').toLowerCase();
    const name = (el.getAttribute('name') || el.name || '').toLowerCase();
    const placeholder = (el.placeholder || '').toLowerCase();
    const className = (typeof el.className === 'string' ? el.className : '').toLowerCase();
    const autocomplete = (el.getAttribute('autocomplete') || '').toLowerCase();
    const dataField = (el.dataset.field || el.dataset.type || '').toLowerCase();

    // Exclude search inputs
    if (id.includes('search') || name.includes('search') || placeholder.includes('search') || className.includes('search')) {
        return false;
    }

    if (autocomplete === 'email') return true;
    if (className.includes('email-input') || className.includes('email-input-field') || className.includes('email-field')) return true;
    if (dataField === 'email' || el.dataset.emailField !== undefined) return true;

    // Match id / name patterns containing 'email'
    const emailPattern = /(?:^|[_-]|client_|service_|accounts_|notification_|signer_|user_|bdm_)email/i;
    if (emailPattern.test(id) || emailPattern.test(name)) return true;

    // Match placeholder patterns
    if (
        placeholder === 'email' ||
        placeholder === 'lead email' ||
        placeholder === 'add recipient' ||
        placeholder === 'add cc recipient' ||
        placeholder === 'add bcc recipient' ||
        placeholder.includes('e.g. john@company.com') ||
        placeholder.includes('john.doe@example.com') ||
        placeholder.includes('type an email')
    ) {
        return true;
    }

    return false;
}

/**
 * Inserts text into an input or textarea element, updating Vue reactivity and firing events.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} target
 * @param {string} text
 */
export function insertTextIntoInput(target, text) {
    if (!target) return;

    if (typeof target.focus === 'function' && typeof document !== 'undefined' && document.activeElement !== target) {
        try {
            target.focus();
        } catch (e) {
            // Ignore focus errors
        }
    }

    const start = (typeof target.selectionStart === 'number') ? target.selectionStart : 0;
    const end = (typeof target.selectionEnd === 'number') ? target.selectionEnd : (target.value ? target.value.length : 0);
    const originalVal = target.value || '';
    const expectedVal = originalVal.slice(0, start) + text + originalVal.slice(end);

    let inserted = false;
    if (typeof document !== 'undefined' && typeof document.execCommand === 'function') {
        try {
            inserted = document.execCommand('insertText', false, text);
        } catch (e) {
            inserted = false;
        }
    }

    if (!inserted || target.value !== expectedVal) {
        target.value = expectedVal;
        const newPos = start + text.length;
        if (typeof target.setSelectionRange === 'function') {
            try {
                target.setSelectionRange(newPos, newPos);
            } catch (e) {
                // Ignore if not supported on specific element
            }
        }
        if (typeof Event !== 'undefined' && typeof target.dispatchEvent === 'function') {
            try {
                target.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
                target.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
            } catch (e) {
                // Ignore dispatch errors
            }
        }
    }
}
